/**
 * "Colour the world" (WOW · colour, live config `enableColourWorld`): the world map starts as a pencil
 * sketch of its painting and blooms back into colour around every completed lesson.
 *
 *   ColourBackdrop  painted maps: sketch layer + colour layer masked by watercolour blots around the
 *                   done nodes (screen positions, follows the scroll + parallax) + a global colour wash
 *                   that grows with % complete; new completions bloom open (stored per profile/world).
 *   ColourVeil      SVG-scene fallback (no painting): a greyscale veil with holes around done nodes.
 *   BloomProps      Storybook props popping around done nodes: flowers, butterflies, sparkles on water,
 *                   autumn leaves, snow, hearts, balloons, and lanterns switching on in the night world.
 *   MapCreatures    the child's living creatures (living agent's store slice) wandering near done nodes;
 *                   fallback = the subjects of the lessons they finished.
 *   ColourChip      "{n}% in colour" progress chip + "Colour is blooming!" ribbon.
 */
import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { hash, type CreatureMeta, type PathId, hasArt, isLightDrawing } from '@sketchling/core';
import { useApp } from '../state/store';
import { useT } from '../lib/i18n';
import { play } from '../lib/sfx';
import { drawingById } from '../lib/content';
import { ArtThumb } from '../art/Thumb';
import { drawingThumbKey } from '../art/thumbKeys';
import { Figure } from '../art/Figure';
import { useArtStyle } from '../art/useArtStyle';
import { Icon } from './icons';
import { LivingCreature, useCreatures } from './living';
import '../styles/pathmap-colour.css';

export interface CwNode {
  key: string;
  x: number;
  y: number;
  /** Node width (scaled). */
  w: number;
  done: boolean;
  level: number;
  /** Round lesson node (props / creatures sit between these). */
  circle: boolean;
  drawingId?: string;
}

const EMPTY_SET: ReadonlySet<string> = new Set();
const EMPTY_LIVING: CreatureMeta[] = [];
const INK = '#4A3226';
const reduced = () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
const rand = (seed: number) => {
  let s = seed >>> 0 || 1;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 4294967296;
  };
};
const easeOutBack = (t: number) => {
  const c = 1.5;
  return 1 + (c + 1) * Math.pow(t - 1, 3) + c * Math.pow(t - 1, 2);
};
const BLOOM_MS = 1500;
const BLOOM_STAGGER = 260;

/* --------------------------------- bloom memory --------------------------------- */

const SESSION_FRESH = new Map<string, { sig: string; fresh: ReadonlySet<string> }>();

/** Done nodes this profile has not seen bloom yet (remembered per profile + world on this device). */
export function useFreshBlooms(profileId: string | null, world: PathId, doneKeys: string[]): ReadonlySet<string> {
  const [fresh, setFresh] = useState<ReadonlySet<string>>(EMPTY_SET);
  const sig = doneKeys.join('|');
  useEffect(() => {
    const key = `sk-cw-bloomed:${profileId ?? 'anon'}:${world}`;
    // Same answer for a repeated effect run (React StrictMode) within this visit.
    const memo = SESSION_FRESH.get(key);
    if (memo && memo.sig === sig) {
      setFresh(memo.fresh);
      return;
    }
    let seen: string[] | null = null;
    try {
      seen = JSON.parse(localStorage.getItem(key) ?? 'null') as string[] | null;
    } catch {
      seen = null;
    }
    const known = new Set(seen ?? []);
    const f = doneKeys.filter((k) => !known.has(k));
    const next: ReadonlySet<string> = f.length ? new Set(f) : EMPTY_SET;
    SESSION_FRESH.set(key, { sig, fresh: next });
    setFresh(next);
    if (f.length)
      try {
        localStorage.setItem(key, JSON.stringify(doneKeys));
      } catch {
        /* storage blocked: the bloom simply replays next time */
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId, world, sig]);
  return fresh;
}

/** Order of a fresh node in the bloom sequence (0 = first). */
const freshOrder = (nodes: CwNode[], fresh: ReadonlySet<string>) => {
  const m = new Map<string, number>();
  let i = 0;
  for (const n of nodes) if (fresh.has(n.key)) m.set(n.key, i++);
  return m;
};

/* -------------------------------- painted backdrop -------------------------------- */

interface Blot {
  x: number;
  y: number;
  r: number;
}

/** Three overlapping soft blots per node = an organic watercolour patch. */
function blots(n: CwNode, R: number): Blot[] {
  const r = rand(hash(n.key));
  return [
    { x: 0, y: 0, r: R },
    {
      x: (0.25 + r() * 0.2) * R,
      y: -(0.15 + r() * 0.2) * R,
      r: R * (0.62 + r() * 0.15),
    },
    {
      x: -(0.25 + r() * 0.2) * R,
      y: (0.1 + r() * 0.2) * R,
      r: R * (0.58 + r() * 0.15),
    },
  ];
}

function maskCss(parts: Blot[], base: number): string {
  const g = parts.map((b) => `radial-gradient(circle ${b.r.toFixed(1)}px at ${b.x.toFixed(1)}px ${b.y.toFixed(1)}px, #000 0, #000 42%, rgba(0,0,0,.55) 68%, transparent 100%)`);
  g.push(`linear-gradient(rgba(0,0,0,${base.toFixed(3)}), rgba(0,0,0,${base.toFixed(3)}))`);
  return g.join(',');
}

export function ColourBackdrop({
  scroller,
  colour,
  sketch,
  scrim,
  nodes,
  fresh,
  progress,
  h,
  s,
}: {
  scroller: RefObject<HTMLDivElement | null>;
  colour: string;
  sketch: string | null;
  scrim: string;
  nodes: CwNode[];
  fresh: ReadonlySet<string>;
  progress: number;
  h: number;
  s: number;
}) {
  const sketchEl = useRef<HTMLDivElement>(null);
  const colourEl = useRef<HTMLDivElement>(null);
  const t0 = useRef(0);
  const raf = useRef(0);
  const order = useMemo(() => freshOrder(nodes, fresh), [nodes, fresh]);
  const done = useMemo(() => nodes.filter((n) => n.done), [nodes]);
  const base = progress >= 1 ? 1 : 0.04 + 0.3 * progress;

  // One painter for scroll, resize and the bloom animation (writes styles directly: no re-render).
  const paintRef = useRef<(now: number) => boolean>(() => false);
  paintRef.current = (now: number) => {
    const el = scroller.current;
    const c = colourEl.current;
    if (!el || !c) return false;
    const left = el.scrollLeft;
    const f = el.scrollWidth > el.clientWidth ? left / (el.scrollWidth - el.clientWidth) : 0;
    const pos = `${(f * 100).toFixed(1)}% center`;
    c.style.backgroundPosition = pos;
    if (sketchEl.current) sketchEl.current.style.backgroundPosition = pos;
    const R = Math.max(130 * s, h * 0.46);
    let animating = false;
    const parts: Blot[] = [];
    for (const n of done) {
      const x = n.x - left;
      if (x < -R * 1.6 || x > el.clientWidth + R * 1.6) continue;
      let k = 1;
      const o = order.get(n.key);
      if (o !== undefined && t0.current) {
        const t = Math.max(0, Math.min(1, (now - t0.current - o * BLOOM_STAGGER) / BLOOM_MS));
        if (t < 1) animating = true;
        k = easeOutBack(t);
      }
      if (k <= 0.01) continue;
      for (const b of blots(n, R * k)) parts.push({ x: x + b.x, y: n.y + b.y, r: b.r });
    }
    const m = maskCss(parts, base);
    c.style.maskImage = m;
    c.style.setProperty('-webkit-mask-image', m);
    return animating;
  };

  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const on = () => {
      if (!raf.current) paintRef.current(performance.now());
    };
    el.addEventListener('scroll', on, { passive: true });
    paintRef.current(performance.now());
    return () => el.removeEventListener('scroll', on);
  }, [scroller, nodes, base, h, s, colour]);

  // Bloom: animate the blots of fresh nodes open.
  useEffect(() => {
    if (!fresh.size || reduced()) {
      t0.current = 0;
      paintRef.current(performance.now());
      return;
    }
    t0.current = performance.now() + 450;
    const tick = (now: number) => {
      const more = paintRef.current(now);
      raf.current = more || now < t0.current ? requestAnimationFrame(tick) : 0;
    };
    raf.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf.current);
      raf.current = 0;
    };
  }, [fresh]);

  return (
    <div className="cw-backdrop" aria-hidden data-testid="cw-backdrop">
      <div ref={sketchEl} className={`cw-layer cw-sketch${sketch ? '' : ' pending'}`} style={{ backgroundImage: `url(${sketch ?? colour})` }} data-sketch={sketch ? 'canvas' : 'css'} />
      <div ref={colourEl} className="cw-layer cw-colour" style={{ backgroundImage: `url(${colour})` }} data-testid="cw-colour" />
      <div className="cw-layer cw-scrim" style={{ background: scrim }} />
    </div>
  );
}

/* ------------------------------ SVG scene fallback ------------------------------ */

/** Greyscale paper veil over the procedural scene with soft holes around done nodes. */
export const ColourVeil = memo(function ColourVeil({ nodes, width, h, s, progress }: { nodes: CwNode[]; width: number; h: number; s: number; progress: number }) {
  const mask = useMemo(() => {
    const R = Math.max(130 * s, h * 0.46);
    const W = Math.round(width);
    const H = Math.round(h);
    const holes = nodes
      .filter((n) => n.done)
      .flatMap((n) => blots(n, R).map((b) => `<circle cx='${(n.x + b.x).toFixed(0)}' cy='${(n.y + b.y).toFixed(0)}' r='${b.r.toFixed(0)}' fill='url(%23g)'/>`))
      .join('');
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${W}' height='${H}'><defs><radialGradient id='g'><stop offset='0.42' stop-color='%23000'/><stop offset='1' stop-color='%23000' stop-opacity='0'/></radialGradient><mask id='m'><rect width='${W}' height='${H}' fill='%23fff'/>${holes}</mask></defs><rect width='${W}' height='${H}' fill='%23fff' mask='url(%23m)'/></svg>`;
    return `url("data:image/svg+xml;utf8,${svg}")`;
  }, [nodes, width, h, s]);
  const g = Math.max(0, 1 - progress * 0.75).toFixed(2);
  return (
    <div
      className="cw-veil"
      aria-hidden
      data-testid="cw-veil"
      style={
        {
          width,
          height: h,
          maskImage: mask,
          WebkitMaskImage: mask,
          backdropFilter: `grayscale(${g}) sepia(.18) brightness(1.06) contrast(.92)`,
          WebkitBackdropFilter: `grayscale(${g}) sepia(.18) brightness(1.06) contrast(.92)`,
        } as CSSProperties
      }
    />
  );
});

/* ---------------------------------- bloom props ---------------------------------- */

type Prop = (x: number, y: number, u: number, key: string) => ReactNode;

const petals = (x: number, y: number, r: number, fill: string, centre: string, k: string) => (
  <g key={k}>
    <path d={`M${x} ${y + r * 0.8}Q${x - r * 0.2} ${y + r * 2.4} ${x} ${y + r * 3.2}`} stroke="#4E8A5C" strokeWidth={r * 0.32} fill="none" strokeLinecap="round" />
    <path
      d={`M${x} ${y + r * 2.4}q${r * 0.9} ${-r * 0.7} ${r * 1.4} ${-r * 0.1}q${-r * 0.6} ${r * 0.6} ${-r * 1.4} ${r * 0.1}z`}
      fill="#7DB86A"
      stroke={INK}
      strokeWidth={r * 0.14}
      strokeLinejoin="round"
    />
    {[0, 72, 144, 216, 288].map((a) => {
      const rad = ((a - 90) * Math.PI) / 180;
      return <circle key={a} cx={x + Math.cos(rad) * r * 0.62} cy={y + Math.sin(rad) * r * 0.62} r={r * 0.52} fill={fill} stroke={INK} strokeWidth={r * 0.14} />;
    })}
    <circle cx={x} cy={y} r={r * 0.36} fill={centre} stroke={INK} strokeWidth={r * 0.12} />
    <circle cx={x - r * 0.12} cy={y - r * 0.12} r={r * 0.1} fill="#FFFDF8" opacity={0.85} />
  </g>
);

const star4 = (x: number, y: number, r: number, fill = '#FFF6C9') =>
  `M${x} ${y - r}Q${x + r * 0.18} ${y - r * 0.18} ${x + r} ${y}Q${x + r * 0.18} ${y + r * 0.18} ${x} ${y + r}Q${x - r * 0.18} ${y + r * 0.18} ${x - r} ${y}Q${x - r * 0.18} ${y - r * 0.18} ${x} ${y - r}Z` +
  (fill ? '' : '');

const PROPS: Record<string, Prop> = {
  flowerA: (x, y, u, k) => petals(x, y, 7 * u, '#F27E7E', '#F7D35C', k),
  flowerB: (x, y, u, k) => petals(x, y, 6.4 * u, '#F7D35C', '#E9783A', k),
  flowerW: (x, y, u, k) => petals(x, y, 6 * u, '#FFFDF8', '#F2C14E', k),
  blossom: (x, y, u, k) => petals(x, y, 6.6 * u, '#F4A3B5', '#FFF1DC', k),
  sunflower: (x, y, u, k) => petals(x, y, 7.4 * u, '#F6C453', '#8A5A3C', k),
  sparkle: (x, y, u, k) => (
    <g key={k} className="cw-twinkle">
      <path d={star4(x, y, 7 * u)} fill="#FFF6C9" stroke="#F2C14E" strokeWidth={1.2 * u} strokeLinejoin="round" />
      <path d={star4(x + 11 * u, y + 7 * u, 3.6 * u)} fill="#FFFFFF" />
    </g>
  ),
  water: (x, y, u, k) => (
    <g key={k} className="cw-twinkle">
      <path d={star4(x, y, 5.5 * u)} fill="#FFFFFF" />
      <path d={star4(x - 10 * u, y + 5 * u, 3.4 * u)} fill="#DDF3F7" />
      <path d={star4(x + 9 * u, y - 4 * u, 2.6 * u)} fill="#FFFFFF" />
    </g>
  ),
  butterfly: (x, y, u, k) => (
    <g key={k} className="cw-flutter">
      <ellipse cx={x - 5 * u} cy={y - 3 * u} rx={5.4 * u} ry={4.4 * u} fill="#4C86C9" stroke={INK} strokeWidth={1.1 * u} />
      <ellipse cx={x + 5 * u} cy={y - 3 * u} rx={5.4 * u} ry={4.4 * u} fill="#4C86C9" stroke={INK} strokeWidth={1.1 * u} />
      <ellipse cx={x - 4 * u} cy={y + 3.4 * u} rx={3.6 * u} ry={3 * u} fill="#F7D35C" stroke={INK} strokeWidth={1.1 * u} />
      <ellipse cx={x + 4 * u} cy={y + 3.4 * u} rx={3.6 * u} ry={3 * u} fill="#F7D35C" stroke={INK} strokeWidth={1.1 * u} />
      <path d={`M${x} ${y - 6 * u}L${x} ${y + 6 * u}`} stroke={INK} strokeWidth={2 * u} strokeLinecap="round" />
    </g>
  ),
  leafA: (x, y, u, k) => (
    <path
      key={k}
      className="cw-fall"
      d={`M${x - 7 * u} ${y + 4 * u}Q${x - 4 * u} ${y - 8 * u} ${x + 8 * u} ${y - 5 * u}Q${x + 5 * u} ${y + 8 * u} ${x - 7 * u} ${y + 4 * u}Z`}
      fill="#D9643A"
      stroke={INK}
      strokeWidth={1.2 * u}
      strokeLinejoin="round"
    />
  ),
  leafB: (x, y, u, k) => (
    <path
      key={k}
      className="cw-fall"
      d={`M${x - 6 * u} ${y + 5 * u}Q${x - 5 * u} ${y - 6 * u} ${x + 7 * u} ${y - 6 * u}Q${x + 6 * u} ${y + 6 * u} ${x - 6 * u} ${y + 5 * u}Z`}
      fill="#F6C453"
      stroke={INK}
      strokeWidth={1.2 * u}
      strokeLinejoin="round"
    />
  ),
  snow: (x, y, u, k) => (
    <g key={k} className="cw-twinkle">
      {[0, 60, 120].map((a) => (
        <path key={a} d={`M${x} ${y - 7 * u}L${x} ${y + 7 * u}`} transform={`rotate(${a} ${x} ${y})`} stroke="#FFFFFF" strokeWidth={2.2 * u} strokeLinecap="round" />
      ))}
      <circle cx={x} cy={y} r={2 * u} fill="#E4ECF4" />
    </g>
  ),
  heart: (x, y, u, k) => (
    <path
      key={k}
      className="cw-beat"
      d={`M${x} ${y + 7 * u}C${x - 10 * u} ${y} ${x - 9 * u} ${y - 9 * u} ${x - 3.5 * u} ${y - 7 * u}C${x - 1.5 * u} ${y - 6.4 * u} ${x} ${y - 4 * u} ${x} ${y - 3 * u}C${x} ${y - 4 * u} ${x + 1.5 * u} ${y - 6.4 * u} ${x + 3.5 * u} ${y - 7 * u}C${x + 9 * u} ${y - 9 * u} ${x + 10 * u} ${y} ${x} ${y + 7 * u}Z`}
      fill="#E5596A"
      stroke={INK}
      strokeWidth={1.2 * u}
      strokeLinejoin="round"
    />
  ),
  sprinkles: (x, y, u, k) => (
    <g key={k}>
      {(['#E5596A', '#9AD3BF', '#F7D35C', '#4C86C9'] as const).map((c, i) => (
        <rect key={c} x={x + (i - 1.5) * 6 * u} y={y + ((i * 7) % 5) * u} width={6 * u} height={2.6 * u} rx={1.3 * u} fill={c} transform={`rotate(${i * 47 - 30} ${x + (i - 1.5) * 6 * u} ${y})`} />
      ))}
    </g>
  ),
  splat: (x, y, u, k) => {
    const c = ['#F2C14E', '#EE8C8C', '#4C86C9'][hash(k) % 3]!;
    return (
      <path
        key={k}
        d={`M${x - 8 * u} ${y}C${x - 9 * u} ${y - 7 * u} ${x - 2 * u} ${y - 9 * u} ${x + 2 * u} ${y - 7 * u}C${x + 8 * u} ${y - 9 * u} ${x + 10 * u} ${y - 2 * u} ${x + 7 * u} ${y + 2 * u}C${x + 9 * u} ${y + 7 * u} ${x + 2 * u} ${y + 9 * u} ${x - 1 * u} ${y + 6 * u}C${x - 7 * u} ${y + 8 * u} ${x - 11 * u} ${y + 4 * u} ${x - 8 * u} ${y}Z`}
        fill={c}
        stroke={INK}
        strokeWidth={1.1 * u}
      />
    );
  },
  balloon: (x, y, u, k) => {
    const c = ['#E9785B', '#5AAFD9', '#F2C14E'][hash(k) % 3]!;
    return (
      <g key={k} className="cw-float">
        <path d={`M${x} ${y + 9 * u}Q${x - 3 * u} ${y + 15 * u} ${x + 1 * u} ${y + 21 * u}`} stroke={INK} strokeWidth={1 * u} fill="none" />
        <ellipse cx={x} cy={y} rx={7.4 * u} ry={9 * u} fill={c} stroke={INK} strokeWidth={1.3 * u} />
        <ellipse cx={x - 2.6 * u} cy={y - 3.4 * u} rx={1.8 * u} ry={2.8 * u} fill="#FFFDF8" opacity={0.7} />
      </g>
    );
  },
  lantern: (x, y, u, k) => (
    <g key={k}>
      <circle className="cw-glow" cx={x} cy={y} r={20 * u} fill="url(#cwGlow)" />
      <path d={`M${x} ${y - 14 * u}L${x} ${y - 9 * u}`} stroke={INK} strokeWidth={1.2 * u} />
      <rect x={x - 6.5 * u} y={y - 9 * u} width={13 * u} height={17 * u} rx={5.5 * u} fill="#FF9E7A" stroke={INK} strokeWidth={1.3 * u} />
      <rect className="cw-light" x={x - 4 * u} y={y - 6 * u} width={8 * u} height={11 * u} rx={3.5 * u} fill="#FFE7A6" />
      <path d={`M${x - 4 * u} ${y + 8 * u}L${x + 4 * u} ${y + 8 * u}`} stroke={INK} strokeWidth={1.6 * u} strokeLinecap="round" />
    </g>
  ),
  grass: (x, y, u, k) => (
    <path
      key={k}
      d={`M${x - 6 * u} ${y + 4 * u}Q${x - 6 * u} ${y - 4 * u} ${x - 9 * u} ${y - 8 * u}M${x} ${y + 4 * u}Q${x} ${y - 6 * u} ${x + 1 * u} ${y - 11 * u}M${x + 6 * u} ${y + 4 * u}Q${x + 6 * u} ${y - 3 * u} ${x + 10 * u} ${y - 7 * u}`}
      stroke="#4E8A5C"
      strokeWidth={2 * u}
      strokeLinecap="round"
      fill="none"
    />
  ),
};

/** Which props bloom around a node, per world (and season / zone). */
function propsFor(world: PathId, level: number, seed: number): string[] {
  const r = rand(seed);
  const pick = <T,>(a: T[]) => a[Math.floor(r() * a.length)]!;
  switch (world) {
    case 'intro':
      return ['splat', 'sparkle', pick(['flowerB', 'flowerA']), 'splat'];
    case 'animals':
      return level === 2 ? ['water', 'flowerA', 'butterfly', 'grass'] : ['flowerA', pick(['flowerB', 'flowerW']), 'butterfly', 'grass'];
    case 'nature':
      return level === 1
        ? ['blossom', 'blossom', 'butterfly', 'grass']
        : level === 2
          ? ['sunflower', 'water', 'flowerA', 'grass']
          : level === 3
            ? ['leafA', 'leafB', 'leafA', 'flowerB']
            : ['snow', 'sparkle', 'snow', 'flowerW'];
    case 'characters':
      return ['balloon', 'flowerA', 'balloon', 'sparkle'];
    case 'anime':
      return ['lantern', 'sparkle', 'lantern', 'blossom'];
    case 'cuteObjects':
      return ['heart', 'sprinkles', 'heart', 'sparkle'];
  }
}

/** Slots around a node (x, y in node-width units, y up < 0), avoiding the disc and the label below. */
const SLOTS: [number, number][] = [
  [-0.82, -0.18],
  [0.84, -0.3],
  [-0.5, -0.78],
  [0.56, -0.82],
];

export const BloomProps = memo(function BloomProps({ world, nodes, fresh, width, h, s }: { world: PathId; nodes: CwNode[]; fresh: ReadonlySet<string>; width: number; h: number; s: number }) {
  const order = freshOrder(nodes, fresh);
  const u = Math.max(0.8, s);
  return (
    <svg className="cw-props" width={width} height={h} aria-hidden data-testid="cw-props">
      <defs>
        <radialGradient id="cwGlow">
          <stop offset="0" stopColor="#FFE7A6" stopOpacity="0.95" />
          <stop offset="0.45" stopColor="#FFD98A" stopOpacity="0.45" />
          <stop offset="1" stopColor="#FFD98A" stopOpacity="0" />
        </radialGradient>
      </defs>
      {nodes.map((n) => {
        if (!n.done) return null;
        const kinds = propsFor(world, n.level, hash(n.key));
        const o = order.get(n.key);
        const W = Math.max(92 * s, Math.min(n.w, 130 * s));
        return (
          <g key={n.key} className={o !== undefined ? 'cw-bloom-new' : 'cw-bloom'} data-node={n.key}>
            {kinds.map((k, i) => {
              const [dx, dy] = SLOTS[i % SLOTS.length]!;
              const x = n.x + dx * W;
              const y = Math.max(16 * s, Math.min(h - 16 * s, n.y + dy * W));
              const style = {
                animationDelay: o !== undefined ? `${0.6 + o * (BLOOM_STAGGER / 1000) + i * 0.11}s` : `${-(hash(n.key + i) % 3000) / 1000}s`,
              } as CSSProperties;
              return (
                <g key={i} className={`cw-prop cw-${k}`} style={style}>
                  {PROPS[k]!(x, y, u, `${n.key}-${i}`)}
                </g>
              );
            })}
          </g>
        );
      })}
    </svg>
  );
});

/* ------------------------------------ creatures ------------------------------------ */

function LessonFriend({ drawingId, size }: { drawingId: string; size: number }) {
  const styleId = useArtStyle();
  const d = drawingById(drawingId);
  // Light drawing (world chunk not loaded): the build-time thumbnail — the map never loads a world's strokes.
  if (d && isLightDrawing(d)) return hasArt(d) ? <ArtThumb thumbKey={drawingThumbKey(d.id)} sig={undefined} style={styleId} imgStyle={{ width: size, height: size, objectFit: 'contain' }} vector={() => null} /> : null;
  if (!d?.art) return null;
  return <Figure def={d.art} styleId={styleId} stage={5} noGround style={{ width: size, height: size }} />;
}

export function MapCreatures({ world, nodes, h, s }: { world: PathId; nodes: CwNode[]; h: number; s: number }) {
  const t = useT();
  const livingOn = useApp((st) => st.config.livingDrawings);
  const mine = useCreatures(world);
  const [hop, setHop] = useState<string | null>(null);
  const living: CreatureMeta[] = livingOn ? mine : EMPTY_LIVING;

  const slots = useMemo(() => {
    // Between consecutive done lesson nodes (and the last done → next), below the trail.
    const lessons = nodes.filter((n) => n.circle || n.drawingId);
    const out: { x: number; y: number; key: string; from?: CwNode }[] = [];
    for (let i = 0; i < lessons.length; i++) {
      const a = lessons[i]!;
      const b = lessons[i + 1];
      if (!a.done) break;
      // Centre of the gap between the two nodes' edges (polaroid cards are wide).
      const x = b ? (a.x + a.w / 2 + (b.x - b.w / 2)) / 2 : a.x + a.w / 2 + 34 * s;
      // In the foreground below the trail, between the two nodes' labels.
      const y = Math.max(34 * s, Math.min(h - 30 * s, Math.max(a.y, b?.y ?? a.y) + 76 * s));
      out.push({ x, y, key: a.key, from: a });
    }
    return out;
  }, [nodes, h, s]);

  // The child's own living creatures first, then the subjects of finished lessons in the free gaps.
  const cast = useMemo(() => {
    const max = Math.min(6, slots.length);
    // Newest gaps first: the friends gather near where the child is now.
    const recent = slots.slice().reverse();
    const out: { slot: (typeof slots)[number]; creature?: CreatureMeta; drawingId?: string }[] = living.slice(0, max).map((c, i) => ({ slot: recent[i]!, creature: c }));
    for (const sl of recent.slice(out.length)) {
      if (out.length >= max) break;
      if (sl.from?.drawingId && hasArt(drawingById(sl.from.drawingId))) out.push({ slot: sl, drawingId: sl.from.drawingId });
    }
    return out;
  }, [slots, living]);

  if (!cast.length) return null;
  const size = Math.round(50 * Math.max(0.85, s));
  return (
    <div className="cw-creatures" aria-label={t('cw.friends')} data-testid="cw-creatures" data-living={living.length ? 'yes' : 'no'}>
      {cast.map(({ slot, creature, drawingId }, i) => {
        if (creature) {
          const bw = Math.round(118 * Math.max(0.85, s));
          const bh = Math.round(70 * Math.max(0.85, s));
          return (
            <div key={creature.id} className="cw-living" style={{ left: slot.x - bw / 2, top: slot.y - bh * 0.62, width: bw, height: bh }}>
              <LivingCreature creature={creature} size={bw} height={bh} roam scale={0.82} label={t('cw.friend', { name: creature.name })} testId="cw-living-creature" />
            </div>
          );
        }
        const id = `${drawingId}-${i}`;
        const dur = 7 + (hash(id) % 400) / 100;
        const style = {
          left: slot.x - size / 2,
          top: slot.y - size / 2,
          width: size,
          height: size,
          ['--cw-wander' as string]: `${Math.round(22 * s)}px`,
          ['--cw-dur' as string]: `${dur}s`,
          animationDelay: `${-(hash(id) % 5000) / 1000}s`,
        } as CSSProperties;
        return (
          <button
            key={id}
            type="button"
            className={`cw-creature k-hopper${hop === id ? ' hop' : ''}`}
            style={style}
            aria-label={t('cw.friends')}
            onClick={() => {
              play('sparkle');
              setHop(id);
              window.setTimeout(() => setHop((h2) => (h2 === id ? null : h2)), 700);
            }}
          >
            <span className="cw-walk" style={{ animationDelay: style.animationDelay }}>
              <span className="cw-body">{drawingId ? <LessonFriend drawingId={drawingId} size={size} /> : null}</span>
            </span>
            <span className="cw-shadow" />
          </button>
        );
      })}
    </div>
  );
}

/* ------------------------------------ progress chip ------------------------------------ */

export function ColourChip({ progress, blooming }: { progress: number; blooming: boolean }) {
  const t = useT();
  const pct = Math.round(progress * 100);
  const [ribbon, setRibbon] = useState(false);
  useEffect(() => {
    if (!blooming) return;
    setRibbon(true);
    const a = window.setTimeout(() => play('magic'), 500);
    const b = window.setTimeout(() => setRibbon(false), 3600);
    return () => {
      window.clearTimeout(a);
      window.clearTimeout(b);
    };
  }, [blooming]);
  return (
    <div className="cw-chip" data-testid="cw-chip" data-progress={pct} title={t('cw.title')}>
      <span className="cw-chip-icon">
        <Icon name="theme" size={16} />
      </span>
      <span className="cw-chip-body">
        <b>{pct >= 100 ? t('cw.allDone') : t('cw.progress', { n: pct })}</b>
        <span className="cw-chip-bar">
          <i style={{ width: `${Math.max(4, pct)}%` }} />
        </span>
      </span>
      {ribbon && (
        <span className="cw-ribbon" data-testid="cw-blooming">
          {t('cw.bloom')}
        </span>
      )}
      {!ribbon && pct === 0 && <span className="cw-hint">{t('cw.hint')}</span>}
    </div>
  );
}
